import {
    ReactElement,
    useCallback,
    memo,
    MouseEvent,
    KeyboardEvent,
    ChangeEvent,
} from "react";
import {
    DraggableProvidedDraggableProps,
    DraggableProvidedDragHandleProps,
} from "@hello-pangea/dnd";
import styled from "styled-components";
import { Item } from "../../model/item";
import { h } from "../../h";
import { ItemContainerStyled } from "./ItemContainerStyled";
import { ItemHidableStyled } from "./ItemHidableStyled";
import { formatTime } from "../../utils/date";
import { DURATION_STEP } from "../../config";
import { LockLine } from "./LockLine";
import { DragHandle } from "../generic/DragHandle";
import { RemoveIcon } from "../generic/RemoveIcon";
import { DurationView } from "./DurationView";
import { LockIcon } from "../generic/LockIcon";
import { UnlockIcon } from "../generic/UnlockIcon";

const ItemLineStyled = styled.div`
    width: 600px;
    display: flex;
    height: 65px;
    margin: auto;
`;

const ItemLineMainStyled = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ItemLineTimeControlsStyled = styled.div`
    display: flex;
`;

const ItemTitleInputStyled = styled.input<{ isCurrent: boolean }>`
    width: 100%;
    font-size: ${({ theme }) => theme.fontL};
    border: none;
    outline: none;
    color: ${({ isCurrent, theme }) =>
        isCurrent ? theme.colorAccent : `inherit`};

    &::placeholder {
        color: ${({ theme }) => theme.colorMain};
        opacity: 0.4;
    }
`;

const DurationEditableContainerStyled = styled.div`
    display: flex;
`;

const ItemIconStyled = styled(ItemHidableStyled)`
    cursor: pointer;
    width: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ItemTimeStyled = styled.div`
    width: 45px;
    margin-right: 15px;
`;

const DurationControlsStyled = styled(ItemHidableStyled)`
    width: 30px;
    cursor: pointer;
    text-align: center;
    user-select: none;

    ${ItemContainerStyled} &:hover {
        opacity: 1;
        font-weight: bold;
    }
`;

export type ItemView = Item & {
    isWarning?: boolean;
    time: number;
};

type ItemProps = {
    item: Item;
    innerRef: any;
    inputRef: any;
    draggableProps: DraggableProvidedDraggableProps;
    dragHandleProps: DraggableProvidedDragHandleProps | undefined;
    index: number;
    onTitleChange: (index: number, item: Item) => void;
    onCreateAfter: (index: number) => void;
    onCreateBefore: (index: number) => void;
    onDurationChange: (index: number, item: Item) => void;
    onLockChange: (index: number, item: Item) => void;
    onRemove: (index: number) => void;
    onOrderChange: (index: number, newIndex: number) => void;
    onFocusItem: (index: number) => void;
    onFocusPrevious: (index: number) => void;
    onFocusNext: (index: number) => void;
    isWarning?: boolean;
    time: number;
    isPast: boolean;
    isCurrent: boolean;
    isFuture: boolean;
};

export const ItemLine = memo(
    ({
        item,
        innerRef,
        inputRef,
        draggableProps,
        dragHandleProps,
        index,
        onTitleChange,
        onCreateAfter,
        onCreateBefore,
        onDurationChange,
        onLockChange,
        onRemove,
        onOrderChange,
        onFocusItem,
        onFocusPrevious,
        onFocusNext,
        isWarning,
        time,
        isPast,
        isCurrent,
    }: ItemProps): ReactElement => {
        const onTitleChangeCallback = useCallback(
            (event: ChangeEvent<HTMLInputElement>) =>
                onTitleChange(index, { ...item, title: event.target.value }),
            [index, item, onTitleChange],
        );
        const onInputKeyPress = useCallback(
            (event: KeyboardEvent<HTMLInputElement>): void => {
                if (event.key === `Enter`) {
                    if (event.shiftKey) {
                        onCreateBefore(index);
                    } else {
                        onCreateAfter(index);
                    }
                }
            },
            [index, onCreateAfter, onCreateBefore],
        );
        const onRemoveClick = useCallback(() => onRemove(index), [
            index,
            onRemove,
        ]);
        const onDurationDecreaseClick = useCallback(
            (event: MouseEvent) => {
                event.preventDefault();
                const durationStep = DURATION_STEP * (event.shiftKey ? 6 : 1);
                onDurationChange(index, {
                    ...item,
                    duration: item.duration - durationStep,
                });
            },
            [index, item, onDurationChange],
        );
        const onDurationIncreaseClick = useCallback(
            (event: MouseEvent) => {
                event.preventDefault();
                const durationStep = DURATION_STEP * (event.shiftKey ? 6 : 1);
                onDurationChange(index, {
                    ...item,
                    duration: item.duration + durationStep,
                });
            },
            [index, item, onDurationChange],
        );
        const onInputKeyDown = useCallback(
            (event: KeyboardEvent<HTMLInputElement>): void => {
                if (event.key === `ArrowDown`) {
                    event.preventDefault();
                    if (event.ctrlKey) {
                        onDurationDecreaseClick(event as any);
                    } else if (event.shiftKey) {
                        onOrderChange(index, index + 1);
                    } else {
                        onFocusNext(index);
                    }
                }
                if (event.key === `ArrowUp`) {
                    event.preventDefault();
                    if (event.ctrlKey) {
                        onDurationIncreaseClick(event as any);
                    } else if (event.shiftKey) {
                        onOrderChange(index, index - 1);
                    } else {
                        onFocusPrevious(index);
                    }
                }
                if (event.key === `Delete` && event.shiftKey) {
                    onRemoveClick();
                }
            },
            [
                index,
                onRemoveClick,
                onFocusNext,
                onFocusPrevious,
                onDurationDecreaseClick,
                onDurationIncreaseClick,
                onOrderChange,
            ],
        );
        const onLockToggleClick = useCallback(
            () =>
                onLockChange(index, { ...item, lock: item.lock ? null : time }),
            [index, item, time, onLockChange],
        );
        const onFocus = useCallback(() => {
            onFocusItem(index);
        }, [onFocusItem, index]);
        const inputOnRef = useCallback(
            (element: HTMLInputElement) => {
                inputRef(element, item.id);
            },
            [item.id, inputRef],
        );
        const onLineLockChange = useCallback(
            (index, newLock) => {
                onLockChange(index, { ...item, lock: newLock });
            },
            [onLockChange, item],
        );
        return h(
            ItemContainerStyled,
            {
                ref: innerRef,
                isWarning,
                isPast,
                ...draggableProps,
            },
            item.lock
                ? h(LockLine, {
                      time: item.lock,
                      index,
                      onLockChange: onLineLockChange,
                  })
                : null,
            h(
                ItemLineStyled,
                {},
                h(DragHandle, { dragHandleProps }),
                h(
                    ItemLineMainStyled,
                    {},
                    h(ItemTitleInputStyled, {
                        ref: inputOnRef,
                        placeholder: `Type new plan here...`,
                        value: item.title,
                        onChange: onTitleChangeCallback,
                        onKeyPress: onInputKeyPress,
                        onKeyDown: onInputKeyDown,
                        onFocus,
                        isCurrent,
                    }),
                    h(
                        ItemLineTimeControlsStyled,
                        {},
                        h(ItemTimeStyled, {}, formatTime(time)),
                        h(
                            DurationEditableContainerStyled,
                            {},
                            h(
                                DurationControlsStyled,
                                {
                                    onClick: onDurationDecreaseClick,
                                },
                                `-`,
                            ),
                            h(DurationView, { duration: item.duration }),
                            h(
                                DurationControlsStyled,
                                {
                                    onClick: onDurationIncreaseClick,
                                },
                                `+`,
                            ),
                        ),
                    ),
                ),
                index !== 0
                    ? h(
                          ItemIconStyled,
                          {
                              onClick: onLockToggleClick,
                          },
                          item.lock ? h(UnlockIcon) : h(LockIcon),
                      )
                    : null,
                h(
                    ItemIconStyled,
                    {
                        onClick: onRemoveClick,
                    },
                    h(RemoveIcon),
                ),
            ),
        );
    },
    // (prev, next) => {
    //     Object.keys(prev).forEach((key) => {
    //         if (prev[key] !== next[key]) {
    //             console.log(key, prev[key], next[key]);
    //         }
    //     });
    //     return false;
    // },
);
